import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';

import { ConfigurationSummary } from 'src/app/models/configuration-summary.model';
import { AuthorizationService } from './auth.service';

export interface Configuration {
  configurationId: string;
  configurationName: string;
  configurationVersion: number;
  configurationSchemaVersion: string;
  publishedBy: string;
  clientVersionPublishedOn: string;
  modifiedDate: number;
  status: string;
  data: any;
}

export interface ConfigurationVersion {
  configurationId: string;
  configurationVersionId: string;
  configurationName: string;
  configurationVersion: number;
  configurationSchemaVersion: string;
  modifiedDate: number;
  createdDate: number;
  publishedBy: string;
  status: string;
  clientVersionPublishedOn: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private baseUrl = '/analysis/v2/configuration';

  constructor(private http: HttpClient, private authService: AuthorizationService) { }

  /**
   * Gets all configuration summaries
   * @returns Observable of ConfigurationSummary array
   */
  getAllConfigurationSummaries(): Observable<ConfigurationSummary[]> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<ConfigurationSummary[]>(`${this.baseUrl}/summary`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets all configurations
   * @returns Observable of Configuration array
   */
  getAllConfigurations(): Observable<Configuration[]> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<Configuration[]>(this.baseUrl);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets a configuration by ID
   * @param id Configuration ID
   * @returns Observable of Configuration
   */
  getConfigurationById(id: string): Observable<Configuration> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<Configuration>(`${this.baseUrl}/${id}`);

    } else {
      return EMPTY;
    }
  }

  /**
   * Gets a specific version of a configuration
   * @param id Configuration ID
   * @param version Version number
   * @returns Observable of Configuration
   */
  getConfigurationByIdAndVersion(id: string, version: number): Observable<Configuration> {
    if (this.authService.isLoggedIn()) {
      const params = new HttpParams().set('version', version.toString());
      return this.http.get<Configuration>(`${this.baseUrl}/${id}`, { params });
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets all versions of a configuration
   * @param id Configuration ID
   * @returns Observable of Configuration array
   */
  getAllConfigurationVersions(configId: string): Observable<ConfigurationVersion[]> {
    return this.http.get<ConfigurationVersion[]>(
      `${this.baseUrl}/${configId}/versions`
    );
  }

  /**
   * Adds a new configuration
   * @param configuration Configuration to add
   * @returns Observable of the added Configuration
   */
  addConfiguration(configuration: Configuration): Observable<Configuration> {
    if (this.authService.isLoggedIn()) {
      return this.http.post<Configuration>(this.baseUrl, configuration);
    } else {
      return EMPTY;
    }
  }

  /**
   * Updates an existing configuration
   * @param id Configuration ID
   * @param configuration Updated configuration
   * @returns Observable of the updated Configuration
   */
  updateConfiguration(id: string, configuration: Configuration): Observable<Configuration> {
    if (this.authService.isLoggedIn()) {
      return this.http.put<Configuration>(`${this.baseUrl}/${id}`, configuration);
    } else {
      return EMPTY;
    }
  }

  /**
   * Partially updates a configuration
   * @param id Configuration ID
   * @param patchOperations Object containing fields to update
   * @returns Observable of the patched Configuration
   */
  patchConfiguration(id: string, patchOperations: any): Observable<Configuration> {
    if (this.authService.isLoggedIn()) {
      return this.http.patch<Configuration>(`${this.baseUrl}/${id}`, patchOperations);
    } else {
      return EMPTY;
    }
  }

/**
 * Deletes a configuration
 * @param id Configuration ID
 * @returns Observable of void
 */
deleteConfiguration(id: string): Observable<void> {
  if (this.authService.isLoggedIn()) {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  } else {
    return EMPTY;
  }
}

/**
 * Deletes all versions of a configuration
 * @param id Configuration ID
 * @returns Observable of void
 */
deleteAllConfigurationVersions(id: string): Observable<void> {
  if (this.authService.isLoggedIn()) {
    const options = { params: new HttpParams().set('deleteAll', 'true') };
    return this.http.delete<void>(`${this.baseUrl}/${id}`, options);
  } else {
    return EMPTY;
  }
}
}